import React, { useEffect, useState, useMemo, useRef } from 'react'
import {
  Modal,
  Input,
  DatePicker,
  Switch,
  Button,
  message,
  InputNumber,
  Select
} from 'antd'
import {
  CaretDownOutlined,
  CaretRightOutlined,
  CheckOutlined
} from '@ant-design/icons'
import './CompetitionModal.scss'
import { api } from '../../../../../../../../services'
import { useSelector, connect } from 'react-redux'
import { Spinner } from '../../../../../../../../components'
import moment from 'moment'
import Badge from '../../../../../../../Courses/Create/nestedComponents/CourseCreateMain/components/Badge'
import actions from '../../../../../../../../store/actions'
import { useTranslation } from 'react-i18next'
const { TextArea } = Input
const { Option } = Select
const { RangePicker } = DatePicker

const defaultPointRules = [
  { action_type: 'login', is_enabled: false, point_value: 10 },
  { action_type: 'quiz_complete', is_enabled: false, point_value: 20 },
  { action_type: 'lesson_complete', is_enabled: false, point_value: 20 },
  { action_type: 'get_certificate', is_enabled: false, point_value: 20 },
  { action_type: 'tip_approved', is_enabled: false, point_value: 20 },
  { action_type: 'tip_liked', is_enabled: false, point_value: 20 }
]
const CompetitionModal = ({
  upload,
  visible,
  onCancel,
  refreshData,
  editCompetitionId
}) => {
  const [competitionFetching, setCompetitionFetching] = useState(false)

  const [competitionData, setCompetitionData] = useState({
    title: '',
    description: '',
    skill_id: null,
    start_at: '',
    end_at: '',
    points_enabled: false,
    point_rules: defaultPointRules,
    badges_enabled: false,
    badge_rules: [
      { badge_id: null, is_enabled: true, min_point: 0, max_point: 100 }
    ],
    levels_enabled: false,

    level_rules: [
      { level_name: 'Level 1', is_enabled: true, min_point: 0, max_point: 100 }
    ],
    participants: []
  })
  const [badgeOptions, setBadgeOptions] = useState([])
  const [expanded, setExpanded] = useState({
    points: false,
    badges: false,
    levels: false
  })

  const [orgUsers, setOrgUsers] = useState([])
  const [step, setStep] = useState(1)
  const [createLoading, setCreateLoading] = useState(false)
  const [skillOptions, setSkillOptions] = useState([])
  const [skillLoading, setSkillLoading] = useState(false)
  const searchTimeout = useRef(null)
  const badgeSearchTimeout = useRef(null)
  const [openBadgeModal, setOpenBadgeModal] = useState(false)
  const [badgeLoading, setBadgeLoading] = useState(false)
  const user = useSelector(state => state.user)
  const activeOrganizationId = useMemo(
    () => user && user.info && user.info.active_organization_id,
    [user && user.info && user.info.active_organization_id]
  )
  const { t } = useTranslation()
  const dedupeById = function(list) {
    var seen = {}
    return (list || []).filter(function(item) {
      if (!item) {
        return false
      }
      var id = item.id
      if (id === null || id === undefined) {
        return false
      }
      if (seen[id]) {
        return false
      }
      seen[id] = true
      return true
    })
  }

  // Add a ref to track if component is mounted
  const isMountedRef = React.useRef(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current)
      }
      if (badgeSearchTimeout.current) {
        clearTimeout(badgeSearchTimeout.current)
      }
    }
  }, [])

  useEffect(() => {
    if (activeOrganizationId) {
      fetchOrgUsers(activeOrganizationId).catch(error => {
        if (isMountedRef.current) {
          message.error('Failed to fetching users!')
          setOrgUsers([])
        }
      })
      getSkillJourneyData('') // Initial fetch with empty search
    }
  }, [activeOrganizationId])

  useEffect(() => {
    fetchOrgBadges().catch(error => {
      if (isMountedRef.current) {
        message.error('Failed to fetching Badges!')
        setBadgeOptions([])
      }
    })
  }, [])

  useEffect(() => {
    if (editCompetitionId) {
      fetchCompetitionDetails(editCompetitionId).catch(error => {
        if (isMountedRef.current) {
          message.error('Failed to fetching competition details!')
        }
      })
    } else {
      resetForm()
    }
  }, [editCompetitionId])

  const fetchCompetitionDetails = async id => {
    try {
      setCompetitionFetching(true)
      const response = await api.leaderboard.getCompetition({
        id,
        date: '',
        from: '',
        to: ''
      })
      const data = response.data

      // Extract badge information from badge_rules and populate badgeOptions
      const badgesFromRules = data.badge_rules
        ? data.badge_rules
            .filter(rule => rule.badge && rule.badge.id)
            .map(rule => rule.badge)
        : []
      setBadgeOptions(prev => dedupeById([...prev, ...badgesFromRules]))
      setSkillOptions(prev => dedupeById([...prev, data.skill]))

      let processedPointRules = defaultPointRules.map(defaultRule => {
        const existingRule = data.point_rules?.find(
          rule => rule.action_type === defaultRule.action_type
        )

        if (existingRule) {
          if (
            existingRule.min_point !== undefined &&
            existingRule.max_point !== undefined
          ) {
            return {
              action_type: existingRule.action_type,
              is_enabled: existingRule.is_enabled,
              point_value: existingRule.max_point || 0
            }
          }
          return existingRule
        } else {
          // If rule doesn't exist, use default with is_enabled: false
          return defaultRule
        }
      })

      setCompetitionData({
        ...data,
        point_rules: processedPointRules,
        start_at: data.start_at ? moment(data.start_at) : null,
        end_at: data.end_at ? moment(data.end_at) : null,
        participants: data.participants.map(p => p.user_id)
      })
      setStep(1)
    } catch (error) {
      throw error
    } finally {
      setCompetitionFetching(false)
    }
  }

  const fetchOrgUsers = async orgId => {
    try {
      let res = await api.organizations.getUsersByOrganizationId(orgId)
      setOrgUsers(res.data || [])
    } catch (error) {
      throw error
    }
  }

  const fetchOrgBadges = async search => {
    try {
      setBadgeLoading(true)
      const response = await api.library.getSopList({
        limit: 100,
        offset: 0,
        type: 'BADGE',
        sort: 'NEWEST',
        search
      })
      const badges = (response && response.data && response.data.rows) || []
      // Filter to only include items with format === 'BADGE'
      const filteredBadges = badges.filter(
        badge => badge.media_format === 'BADGE'
      )
      setBadgeOptions(filteredBadges)
      setBadgeLoading(false)
    } catch (error) {
      setBadgeLoading(false)
      throw error
    }
  }

  const handleBadgeSearch = value => {
    if (badgeSearchTimeout.current) {
      clearTimeout(badgeSearchTimeout.current)
    }
    badgeSearchTimeout.current = setTimeout(() => {
      fetchOrgBadges(value)
    }, 400)
  }
  const handleInputChange = (field, value) => {
    setCompetitionData(prev => ({
      ...prev,
      [field]: value == null ? null : value
    }))
  }
  const getSkillJourneyData = search => {
    setSkillLoading(true)
    api.skillJourney
      .listSkillJourneys({
        limit: 10,
        offset: 0,
        search,
        mode: 'competition'
      })
      .then(res => {
        const skills = (res.data && res.data.data) || []
        setSkillOptions(function(prev) {
          var list = [].concat(skills || [])
          var selectedId = competitionData && competitionData.skill_id
          if (selectedId !== null && selectedId !== undefined) {
            var selectedFromPrev = (prev || []).find(function(it) {
              return it && it.id === selectedId
            })
            if (selectedFromPrev) {
              list = list.concat([selectedFromPrev])
            }
          }

          return dedupeById(list)
        })
        setSkillLoading(false)
      })
      .catch(err => {
        message.error('Failed to fetching skill journey!')
        setSkillLoading(false)
      })
  }

  const handleSkillSearch = value => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current)
    }
    searchTimeout.current = setTimeout(() => {
      getSkillJourneyData(value)
    }, 400)
  }

  const handleDateRangeChange = dates => {
    if (dates && dates.length === 2) {
      setCompetitionData(prev => ({
        ...prev,
        start_at: dates[0],
        end_at: dates[1]
      }))
    }
  }

  const handlePointRuleChange = (actionType, field, value) => {
    setCompetitionData(prev => ({
      ...prev,
      point_rules: prev.point_rules.map(rule =>
        rule.action_type === actionType ? { ...rule, [field]: value } : rule
      )
    }))
  }

  const handleBadgeRuleChange = (index, field, value) => {
    setCompetitionData(prev => ({
      ...prev,
      badge_rules: prev.badge_rules.map((rule, i) => {
        if (i !== index) return rule

        const updatedRule = { ...rule, [field]: value }

        // Validate min_point cannot exceed max_point
        if (field === 'min_point' && updatedRule.max_point !== undefined) {
          if (value > updatedRule.max_point) {
            message.warning('Minimum point cannot exceed maximum point')
            return rule // Return original rule without changes
          }
        }

        if (field === 'max_point' && updatedRule.min_point !== undefined) {
          if (updatedRule.min_point > value) {
            message.warning('Maximum point cannot be less than minimum point')
            return rule // Return original rule without changes
          }
        }

        return updatedRule
      })
    }))
  }

  const handleLevelRuleChange = (index, field, value) => {
    setCompetitionData(prev => ({
      ...prev,
      level_rules: prev.level_rules.map((rule, i) => {
        if (i !== index) return rule

        const updatedRule = { ...rule, [field]: value }

        // Validate min_point cannot exceed max_point
        if (field === 'min_point' && updatedRule.max_point !== undefined) {
          if (value > updatedRule.max_point) {
            message.warning('Minimum point cannot exceed maximum point')
            return rule // Return original rule without changes
          }
        }

        if (field === 'max_point' && updatedRule.min_point !== undefined) {
          if (updatedRule.min_point > value) {
            message.warning('Maximum point cannot be less than minimum point')
            return rule // Return original rule without changes
          }
        }

        return updatedRule
      })
    }))
  }

  const addBadgeRule = () => {
    setCompetitionData(prev => ({
      ...prev,
      badge_rules: [
        ...prev.badge_rules,
        { badge_id: null, is_enabled: true, min_point: 0, max_point: 100 }
      ]
    }))
  }

  const removeBadgeRule = index => {
    setCompetitionData(prev => ({
      ...prev,
      badge_rules: prev.badge_rules.filter((_, i) => i !== index)
    }))
  }

  const addLevelRule = () => {
    setCompetitionData(prev => ({
      ...prev,
      level_rules: [
        ...(prev.level_rules || []),
        {
          level_name: `Level ${(prev.level_rules &&
            prev.level_rules.length + 1) ||
            1}`,
          is_enabled: true,
          min_point: 0,
          max_point: 100
        }
      ]
    }))
  }

  const removeLevelRule = index => {
    setCompetitionData(prev => ({
      ...prev,
      level_rules: prev.level_rules.filter((_, i) => i !== index)
    }))
  }

  const toggleSection = key => {
    setExpanded(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const validateStep = currentStep => {
    if (currentStep === 1) {
      if (!competitionData.title.trim()) {
        message.error(t('v5:competition_title_is_required'))
        return false
      }
      if (!competitionData.description.trim()) {
        message.error(t('v5:competition_description_is_required'))
        return false
      }
      if (!competitionData.skill_id) {
        message.error(t('v5:skill_selection_is_required'))
        return false
      }
      if (!competitionData.start_at) {
        message.error(t('v5:start_date_is_required'))
        return false
      }
      if (!competitionData.end_at) {
        message.error(t('v5:end_date_is_required'))
        return false
      }
      if (
        competitionData.start_at &&
        competitionData.end_at &&
        competitionData.start_at.isAfter(competitionData.end_at)
      ) {
        message.error(t('v5:start_date_must_be_before_end_date'))
        return false
      }
    }

    if (currentStep === 2) {
      // Validate point rules
      const enabledPointRules = competitionData.point_rules.filter(
        rule => rule.is_enabled
      )
      for (const rule of enabledPointRules) {
        if (rule.point_value < 0) {
          message.error(
            `${actionTypeLabels[rule.action_type]}: ${t(
              'v5:points_cannot_be_negative'
            )}`
          )
          return false
        }
        if (rule.point_value > 1000) {
          message.error(
            `${actionTypeLabels[rule.action_type]}: ${t(
              'v5:points_connot_exceed'
            )}`
          )
          return false
        }
      }
    }

    return true
  }

  const handleSave = () => {
    if (!validateStep(step)) {
      return
    }

    if (step === 1) {
      setStep(2)
    } else if (step === 2) {
      setStep(3)
    } else if (step === 3) {
      handleSaveCompetition()
    }
  }

  const handleSaveCompetition = async () => {
    try {
      // Prepare the payload using the actual form data
      const payload = {
        title: competitionData.title,
        description: competitionData.description,
        skill_id: competitionData.skill_id,
        start_at: competitionData.start_at
          ? competitionData.start_at.toISOString()
          : null,
        end_at: competitionData.end_at
          ? competitionData.end_at.toISOString()
          : null,
        points_enabled: competitionData.points_enabled,
        badges_enabled: competitionData.badges_enabled,
        levels_enabled: competitionData.levels_enabled,
        point_rules: competitionData.point_rules
          .filter(rule => rule.is_enabled)
          .map(rule => ({
            action_type: rule.action_type,
            is_enabled: rule.is_enabled,
            point_value: rule.point_value
          })),
        badge_rules: (competitionData.badge_rules || []).filter(
          rule => rule && rule.is_enabled && rule.badge_id
        ),
        level_rules: (competitionData.level_rules || []).filter(
          rule => rule && rule.is_enabled
        ),
        participants: competitionData.participants
      }

      // Add ID for edit mode
      if (editCompetitionId) {
        payload.id = editCompetitionId
      }
      setCreateLoading(true)
      const response = editCompetitionId
        ? await api.leaderboard.updateCompetition(payload)
        : await api.leaderboard.createCompetition(payload)

      if (!isMountedRef.current) return

      if (response.data.errorMessage) {
        throw new Error(response.data.errorMessage)
      } else {
        refreshData()
        message.success(
          editCompetitionId
            ? t('v5:competition_updated_successfully')
            : t('v5:competition_created_successfully')
        )
        handleCancel()
      }
    } catch (error) {
      if (isMountedRef.current) {
        message.error(t('v5:error_occured_while_creating_the_competition'))
      }
    } finally {
      if (isMountedRef.current) {
        setCreateLoading(false)
      }
    }
  }

  const resetForm = () => {
    setCompetitionData({
      title: '',
      description: '',
      skill_id: null,
      start_at: null,
      end_at: null,
      points_enabled: false,
      badges_enabled: false,
      levels_enabled: false,
      point_rules: [
        { action_type: 'login', is_enabled: true, point_value: 10 },
        { action_type: 'quiz_complete', is_enabled: true, point_value: 20 },
        { action_type: 'lesson_complete', is_enabled: true, point_value: 20 },
        { action_type: 'get_certificate', is_enabled: true, point_value: 20 },
        { action_type: 'tip_approved', is_enabled: false, point_value: 20 },
        { action_type: 'tip_liked', is_enabled: false, point_value: 20 }
      ],
      badge_rules: [
        { badge_id: null, is_enabled: true, min_point: 0, max_point: 100 }
      ],
      level_rules: [
        {
          level_name: 'Level 1',
          is_enabled: true,
          min_point: 0,
          max_point: 100
        }
      ],
      participants: []
    })
    setStep(1)
    setExpanded({
      points: false,
      badges: false,
      levels: false
    })
  }

  const handleCancel = () => {
    setBadgeOptions([])
    resetForm()
    onCancel()
  }

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1)
    } else {
      handleCancel()
    }
  }

  const steps = [
    { number: 1, label: t('v5:general_information') },
    { number: 2, label: t('v5:gamification') },
    { number: 3, label: t('labels:participants') }
  ]

  const actionTypeLabels = {
    login: t('v3:login'),
    quiz_complete: t('v5:quiz_complete'),
    lesson_complete: t('v5:lesson_complete'),
    get_certificate: t('v5:get_certificate'),
    tip_approved: t('v4:tip_approved'),
    tip_liked: t('v4:tip_liked')
  }
  const badgePresentationUpload = (body, imageType) => {
    setOpenBadgeModal(false)
    setBadgeLoading(true)
    upload(body)
      .then(res => {
        const { id, title, link } = res
        const newBadge = { id, title, link }

        setBadgeOptions(prev => {
          const existingIds = new Set(prev.map(badge => badge.id))
          if (existingIds.has(newBadge.id)) {
            return prev
          }
          return [...prev, newBadge]
        })

        const emptyBadgeRuleIndex = competitionData.badge_rules.findIndex(
          rule => !rule.badge_id
        )

        if (emptyBadgeRuleIndex !== -1) {
          // Update the empty badge rule with the new badge
          handleBadgeRuleChange(emptyBadgeRuleIndex, 'badge_id', id)
        } else {
          // Add a new badge rule with the uploaded badge
          setCompetitionData(prev => ({
            ...prev,
            badge_rules: [
              ...prev.badge_rules,
              { badge_id: id, is_enabled: true, min_point: 0, max_point: 100 }
            ]
          }))
        }

        setBadgeLoading(false)
      })
      .catch(() => {
        setBadgeLoading(false)
        message.error('Error occured. Try Again !')
      })
  }

  return (
    <Modal
      title={
        editCompetitionId
          ? t('v4:edit_competition')
          : t('v4:create_competition')
      }
      visible={visible}
      onCancel={handleCancel}
      footer={[
        step !== 1 && (
          <Button
            icon="arrow-left"
            key="discard"
            onClick={handleBack}
            disabled={
              step === 1 || createLoading || competitionFetching || badgeLoading
            }
          >
            {t('buttons:back')}
          </Button>
        ),
        <Button
          icon={step === 2 ? 'save' : 'arrow-right'}
          key="save"
          type="primary"
          onClick={handleSave}
          style={{ color: 'white' }}
          loading={createLoading || competitionFetching || badgeLoading}
        >
          {step === 3
            ? editCompetitionId
              ? t('v4:update_competition')
              : t('v4:create_competition')
            : t('buttons:next')}
        </Button>
      ]}
      width={640}
      maskClosable={false}
      closable={!createLoading}
      centered
      className="create-competition-modal"
    >
      {openBadgeModal && (
        <Modal
          style={{ top: 20 }}
          bodyStyle={{ height: 'auto', maxHeight: '90vh', overflowY: 'auto' }}
          visible={openBadgeModal}
          onCancel={() => setOpenBadgeModal(false)}
          footer={null}
          width="1229px"
          afterVisibleChange={visible => {
            if (visible) {
              // Scroll to top when modal becomes visible
              setTimeout(() => {
                const modalBody = document.querySelector('.ant-modal-body')
                if (modalBody) {
                  modalBody.scrollTop = 0
                }

                const badgeModalWrap = document.querySelector(
                  '.badge-creator-modal-wrap'
                )
                if (badgeModalWrap) {
                  badgeModalWrap.scrollTop = 0
                }
              }, 100)
            }
          }}
        >
          <Badge
            courseBadgeId={''}
            onUploadImage={body => {
              badgePresentationUpload(body, 'Badge')
            }}
          />
        </Modal>
      )}
      {competitionFetching ? (
        <Spinner size="small" />
      ) : (
        <>
          <div className="competition-steps">
            {steps.map((stepItem, index) => (
              <div
                key={index}
                className={`step-item ${
                  step === stepItem.number ? 'active' : ''
                } ${step > stepItem.number ? 'completed' : ''}`}
              >
                <div className="step-number">
                  {step > stepItem.number ? (
                    <CheckOutlined />
                  ) : (
                    <>{stepItem.number}</>
                  )}
                </div>
                <div className="step-label">{stepItem.label}</div>
              </div>
            ))}
          </div>

          <div className="modal-content">
            {step === 1 && (
              <div className="general-info">
                <div className="form-item">
                  <label>{t('v4:competition_title')}</label>
                  <Input
                    value={competitionData.title}
                    onChange={e => handleInputChange('title', e.target.value)}
                    placeholder={t('v4:enter_competition_title')}
                  />
                </div>

                <div className="form-item">
                  <label>{t('v3:video_des')}</label>
                  <TextArea
                    value={competitionData.description}
                    onChange={e =>
                      handleInputChange('description', e.target.value)
                    }
                    placeholder={t('v4:enter_competition_description')}
                    rows={3}
                  />
                </div>

                <div className="form-item">
                  <label>{t('v4:leaderboard_skill')}</label>
                  <Select
                    defaultValue={competitionData.skill_id}
                    onChange={value =>
                      handleInputChange(
                        'skill_id',
                        value === undefined ? null : value
                      )
                    }
                    style={{ width: '100%' }}
                    allowClear
                    placeholder={t('v4:select_a_skill')}
                    showSearch
                    onSearch={handleSkillSearch}
                    filterOption={false}
                    notFoundContent={
                      skillLoading ? (
                        <div style={{ textAlign: 'center', padding: '10px 0' }}>
                          <Spinner height={30} />
                        </div>
                      ) : (
                        <div
                          style={{
                            textAlign: 'center',
                            padding: '10px 0',
                            color: '#999'
                          }}
                        >
                          {t('v4:no_skills_found')}
                        </div>
                      )
                    }
                  >
                    {(skillOptions || [])
                      .filter(function(s) {
                        return s && s.id !== null && s.id !== undefined
                      })
                      .map(function(skill) {
                        return (
                          <Option key={skill.id} value={skill.id}>
                            {skill.title}
                          </Option>
                        )
                      })}
                  </Select>
                </div>

                <div className="form-item">
                  <label>{t('v4:competition_period')}</label>
                  <RangePicker
                    value={[competitionData.start_at, competitionData.end_at]}
                    onChange={handleDateRangeChange}
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD, HH:mm"
                    showTime
                    disabledDate={current => {
                      // Disable past dates
                      return current && current < moment().startOf('day')
                    }}
                    placeholder={['Start Date', 'End Date']}
                  />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="gamification-step">
                {/* Points Section */}
                <div className="gamification-section">
                  <div
                    className="section-header"
                    onClick={() => toggleSection('points')}
                  >
                    <div className="section-left">
                      <Switch
                        checked={competitionData.points_enabled}
                        onChange={checked =>
                          handleInputChange('points_enabled', checked)
                        }
                      />
                      <span className="section-title">{t('v4:points')}</span>
                    </div>
                    <div className="section-chevron">
                      {expanded.points ? (
                        <CaretDownOutlined />
                      ) : (
                        <CaretRightOutlined />
                      )}
                    </div>
                  </div>
                  {expanded.points && (
                    <div className="section-content">
                      {competitionData.point_rules.map(rule => (
                        <div key={rule.action_type} className="rule-row">
                          <div className="rule-left">
                            <Switch
                              checked={rule.is_enabled}
                              onChange={checked =>
                                handlePointRuleChange(
                                  rule.action_type,
                                  'is_enabled',
                                  checked
                                )
                              }
                            />
                            <span className="rule-label">
                              {actionTypeLabels[rule.action_type]}
                            </span>
                          </div>
                          <div className="rule-right">
                            <InputNumber
                              min={0}
                              placeholder="e.g. 25"
                              value={rule.point_value}
                              onChange={value =>
                                handlePointRuleChange(
                                  rule.action_type,
                                  'point_value',
                                  value
                                )
                              }
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Badges Section */}
                <div className="gamification-section">
                  <div
                    className="section-header"
                    onClick={() => toggleSection('badges')}
                  >
                    <div className="section-left">
                      <Switch
                        checked={competitionData.badges_enabled}
                        onChange={checked => {
                          handleInputChange('badges_enabled', checked)
                          if (
                            checked &&
                            competitionData.badge_rules.length === 0
                          ) {
                            addBadgeRule()
                          }
                        }}
                      />
                      <span className="section-title">{t('v2:badges')}</span>
                    </div>
                    <div className="section-chevron">
                      {expanded.badges ? (
                        <CaretDownOutlined />
                      ) : (
                        <CaretRightOutlined />
                      )}
                    </div>
                  </div>
                  {expanded.badges && (
                    <div className="section-content">
                      {competitionData.badge_rules.map((badge, index) => (
                        <div
                          key={index}
                          className="rule-item"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '8px 16px'
                          }}
                        >
                          <Switch
                            checked={badge.is_enabled}
                            onChange={checked =>
                              handleBadgeRuleChange(
                                index,
                                'is_enabled',
                                checked
                              )
                            }
                          />
                          <Select
                            value={badge.badge_id}
                            onChange={value =>
                              handleBadgeRuleChange(index, 'badge_id', value)
                            }
                            allowClear
                            showSearch
                            size="large"
                            filterOption={false}
                            onSearch={handleBadgeSearch}
                            placeholder="Select badge"
                            style={{ flex: '1', minWidth: '200px' }}
                            loading={badgeLoading}
                            optionLabelProp="label"
                          >
                            {badgeOptions
                              .filter(badgeOption => {
                                // Get all currently selected badge IDs except the current one
                                const selectedBadgeIds = competitionData.badge_rules
                                  .map((rule, ruleIndex) =>
                                    ruleIndex !== index ? rule.badge_id : null
                                  )
                                  .filter(id => id !== null && id !== undefined)

                                // Only show badges that are not already selected (or the current one)
                                return (
                                  !selectedBadgeIds.includes(badgeOption.id) ||
                                  badgeOption.id === badge.badge_id
                                )
                              })
                              .map((badgeOption, optionIndex) => (
                                <Option
                                  key={`${badgeOption.id}-${optionIndex}`}
                                  value={badgeOption.id}
                                  label={
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px'
                                      }}
                                    >
                                      <img
                                        src={badgeOption.link}
                                        alt={badgeOption.title}
                                        style={{
                                          width: '36px',
                                          height: '36px',
                                          borderRadius: '50%',
                                          objectFit: 'cover'
                                        }}
                                      />
                                      {badgeOption.title}
                                    </div>
                                  }
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px'
                                    }}
                                  >
                                    <img
                                      src={badgeOption.link}
                                      alt={badgeOption.title}
                                      style={{
                                        width: '36px',
                                        height: '36px',
                                        borderRadius: '50%',
                                        objectFit: 'cover'
                                      }}
                                    />
                                    <span style={{ fontSize: '14px' }}>
                                      {badgeOption.title}
                                    </span>
                                  </div>
                                </Option>
                              ))}
                          </Select>
                          <InputNumber
                            min={0}
                            value={badge.min_point}
                            onChange={value =>
                              handleBadgeRuleChange(index, 'min_point', value)
                            }
                            placeholder="0"
                            style={{ width: '80px' }}
                          />
                          <InputNumber
                            min={0}
                            value={badge.max_point}
                            onChange={value =>
                              handleBadgeRuleChange(index, 'max_point', value)
                            }
                            placeholder="100"
                            style={{ width: '80px' }}
                          />
                          <Button
                            type="text"
                            danger
                            icon="delete"
                            onClick={() => removeBadgeRule(index)}
                            size="small"
                          />
                        </div>
                      ))}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginTop: '8px',
                          gap: '8px'
                        }}
                      >
                        <Button
                          type="dashed"
                          onClick={addBadgeRule}
                          disabled={badgeLoading}
                          icon="plus"
                          className="add-rule-btn"
                        >
                          {t('v4:add_badge_rule')}
                        </Button>
                        <Button
                          loading={badgeLoading}
                          onClick={() => setOpenBadgeModal(true)}
                          icon="plus"
                          className="add-rule-btn"
                        >
                          {t('v4:create_badge')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Levels Section */}
                <div className="gamification-section">
                  <div
                    className="section-header"
                    onClick={() => toggleSection('levels')}
                  >
                    <div className="section-left">
                      <Switch
                        checked={competitionData.levels_enabled}
                        onChange={checked => {
                          handleInputChange('levels_enabled', checked)
                          if (
                            checked &&
                            competitionData.level_rules.length === 0
                          ) {
                            addLevelRule()
                          }
                        }}
                      />
                      <span className="section-title">{t('v4:levels')}</span>
                    </div>
                    <div className="section-chevron">
                      {expanded.levels ? (
                        <CaretDownOutlined />
                      ) : (
                        <CaretRightOutlined />
                      )}
                    </div>
                  </div>
                  {expanded.levels && (
                    <div className="section-content">
                      {Array.isArray(competitionData.level_rules) &&
                        competitionData.level_rules.map((level, index) => (
                          <div
                            key={index}
                            className="rule-item"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              padding: '8px 16px'
                            }}
                          >
                            <Switch
                              checked={level.is_enabled}
                              onChange={checked =>
                                handleLevelRuleChange(
                                  index,
                                  'is_enabled',
                                  checked
                                )
                              }
                            />
                            <span
                              className="rule-label"
                              style={{ flex: '1', minWidth: '120px' }}
                            >
                              {level.level_name}
                            </span>
                            <InputNumber
                              min={0}
                              value={level.min_point}
                              onChange={value =>
                                handleLevelRuleChange(index, 'min_point', value)
                              }
                              placeholder="0"
                              style={{ width: '80px' }}
                            />
                            <span style={{ fontSize: '12px', color: '#666' }}>
                              to
                            </span>
                            <InputNumber
                              min={0}
                              value={level.max_point}
                              onChange={value =>
                                handleLevelRuleChange(index, 'max_point', value)
                              }
                              placeholder="100"
                              style={{ width: '80px' }}
                            />
                            <Button
                              type="text"
                              danger
                              icon="delete"
                              onClick={() => removeLevelRule(index)}
                              size="small"
                            />
                          </div>
                        ))}
                      <Button
                        type="dashed"
                        onClick={addLevelRule}
                        icon="plus"
                        className="add-rule-btn"
                        style={{ marginTop: '8px' }}
                      >
                        {t('v4:add_level_rule')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="participants-step">
                <div className="form-item">
                  <label>{t('labels:participants')}</label>
                  <Select
                    mode="multiple"
                    value={competitionData.participants}
                    onChange={values =>
                      handleInputChange('participants', values)
                    }
                    placeholder={t('v5:select_participants')}
                    style={{ width: '100%' }}
                    showSearch
                    filterOption={(input, option) => {
                      if (!option || !option.props) return false
                      const searchable = (
                        option.props['data-search'] ||
                        option.props.children ||
                        ''
                      )
                        .toString()
                        .toLowerCase()
                      const term = (input || '').toLowerCase()
                      return searchable.indexOf(term) >= 0
                    }}
                  >
                    {orgUsers.map(obj => {
                      const user = obj.user || {}
                      const userName =
                        user.first_name && user.last_name
                          ? `${user.first_name} ${user.last_name}`
                          : user.username || user.email || 'Unknown User'
                      const searchAggregate = [
                        user.first_name,
                        user.last_name,
                        userName,
                        user.username,
                        user.email
                      ]
                        .filter(Boolean)
                        .join(' ')

                      return (
                        <Option
                          key={user.id}
                          value={user.id}
                          data-search={searchAggregate}
                        >
                          {userName}
                        </Option>
                      )
                    })}
                  </Select>
                </div>
                <div className="participants-info">
                  <p>
                    {t('v4:selected_participants')}:{' '}
                    {competitionData.participants.length}
                  </p>
                  <div className="participants-list">
                    {competitionData.participants.map((id, i) => {
                      const participant = orgUsers.find(
                        user => user.user_id === id
                      )
                      const user = participant ? participant.user : {}
                      const userName =
                        user.first_name && user.last_name
                          ? `${user.first_name} ${user.last_name}`
                          : user.username || user.email || 'Unknown User'
                      return (
                        <div
                          key={`participant-${id}-${i}`}
                          className="participant-item"
                        >
                          <span>{userName}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </Modal>
  )
}
const mapDispatchToProps = dispatch => ({
  upload: (file, opts) => dispatch(actions.library.upload(file, opts))
})

export default connect(null, mapDispatchToProps)(CompetitionModal)
